import React, { useState } from 'react';
import { LifeBuoyIcon, MailIcon, PhoneIcon, ClockIcon, TicketIcon, HelpCircleIcon, ChevronDownIcon, BookOpenIcon, FileTextIcon, CodeIcon, GitBranchIcon, VideoIcon, SendIcon } from 'lucide-react';
export function Support() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const faqs = [{
    question: 'How do I generate a prepaid electricity token?',
    answer: 'Navigate to the Vending > Purchase Prepaid section. Enter the meter number, select the amount in ZAR, and click "Generate Token". The system will validate the meter and generate a 20-digit STS token.'
  }, {
    question: 'What should I do if a meter goes offline?',
    answer: 'First, check the Fault Detection dashboard to see if there is a known area outage. If isolated, the meter may have lost cellular connectivity. The system will automatically attempt to reconnect. If it remains offline for 24 hours, a technician should be dispatched.'
  }, {
    question: 'How are vendor commissions calculated?',
    answer: 'Vendor commissions are calculated automatically based on the percentage set in their profile (typically 5%). This is deducted from the gross sales amount before settlement. You can view detailed breakdowns in the Commissions report.'
  }, {
    question: 'How do I add a new vendor to the system?',
    answer: 'Go to Vendors > Add New Vendor. Fill in the required business details, assign a commission rate, and set their geographical operating area. Once approved, they will receive their API credentials.'
  }, {
    question: 'What does the Grid Balance module do?',
    answer: 'The Grid Balance module compares the total energy supplied to a substation against the sum of energy consumed by all smart meters connected to that substation. Significant discrepancies indicate technical losses or potential theft.'
  }];
  const handleTicketSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setTimeout(() => setIsSubmitted(false), 3000);
    }, 1000);
  };
  return <div className="p-8 max-w-7xl mx-auto animate-in fade-in duration-500" data-id="element-2601">
      {/* Header */}
      <div className="mb-8" data-id="element-2602">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-2603">Support</h1>
        <p className="text-gray-500" data-id="element-2604">
          Get help, report issues, and access documentation.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2605">
        {/* Left Column: Contact & Form */}
        <div className="lg:col-span-1 space-y-8" data-id="element-2606">
          {/* Contact Support Card */}
          <div className="bg-white rounded-none shadow-sm border border-gray-200" data-id="element-2607">
            <div className="p-6 border-b border-gray-100 flex items-center gap-3 bg-gray-50/50" data-id="element-2608">
              <LifeBuoyIcon className="w-5 h-5 text-mavis-teal" data-id="element-2609" />
              <h2 className="text-lg font-bold text-gray-900" data-id="element-2610">
                Contact Support
              </h2>
            </div>
            <div className="p-6 space-y-6" data-id="element-2611">
              <div className="flex items-center gap-4" data-id="element-2612">
                <div className="w-10 h-10 bg-mavis-teal/10 flex items-center justify-center shrink-0" data-id="element-2613">
                  <MailIcon className="w-5 h-5 text-mavis-teal" data-id="element-2614" />
                </div>
                <div data-id="element-2615">
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-0.5" data-id="element-2616">
                    Email Support
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-2617">
                    support@mavis.co.za
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4" data-id="element-2618">
                <div className="w-10 h-10 bg-mavis-teal/10 flex items-center justify-center shrink-0" data-id="element-2619">
                  <PhoneIcon className="w-5 h-5 text-mavis-teal" data-id="element-2620" />
                </div>
                <div data-id="element-2621">
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-0.5" data-id="element-2622">
                    Phone Support
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-2623">
                    +27 11 555 0100
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4" data-id="element-2624">
                <div className="w-10 h-10 bg-mavis-teal/10 flex items-center justify-center shrink-0" data-id="element-2625">
                  <ClockIcon className="w-5 h-5 text-mavis-teal" data-id="element-2626" />
                </div>
                <div data-id="element-2627">
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-0.5" data-id="element-2628">
                    Operating Hours
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-2629">
                    Mon-Fri, 08:00 - 17:00 SAST
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Ticket Card */}
          <div className="bg-white rounded-none shadow-sm border border-gray-200" data-id="element-2630">
            <div className="p-6 border-b border-gray-100 flex items-center gap-3 bg-gray-50/50" data-id="element-2631">
              <TicketIcon className="w-5 h-5 text-mavis-teal" data-id="element-2632" />
              <h2 className="text-lg font-bold text-gray-900" data-id="element-2633">
                Submit a Support Ticket
              </h2>
            </div>
            <div className="p-6" data-id="element-2634">
              {isSubmitted ? <div className="bg-mavis-green/10 border border-mavis-green/20 p-6 text-center" data-id="element-2635">
                  <div className="w-12 h-12 bg-mavis-green/20 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-2636">
                    <TicketIcon className="w-6 h-6 text-mavis-green" data-id="element-2637" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-2638">
                    Ticket Submitted
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-2639">
                    Your request has been received. We will respond shortly.
                  </p>
                </div> : <form onSubmit={handleTicketSubmit} className="space-y-4" data-id="element-2640">
                  <div data-id="element-2641">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2642">
                      Subject
                    </label>
                    <input type="text" required className="w-full px-3 py-2 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal text-sm" placeholder="Brief description of the issue" data-id="element-2643" />

                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-2644">
                    <div data-id="element-2645">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2646">
                        Category
                      </label>
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal text-sm bg-white" data-id="element-2647">
                        <option data-id="element-2648">Billing Issue</option>
                        <option data-id="element-2649">Technical Problem</option>
                        <option data-id="element-2650">Meter Fault</option>
                        <option data-id="element-2651">Account Access</option>
                        <option data-id="element-2652">Feature Request</option>
                        <option data-id="element-2653">Other</option>
                      </select>
                    </div>
                    <div data-id="element-2654">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2655">
                        Priority
                      </label>
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal text-sm bg-white" data-id="element-2656">
                        <option data-id="element-2657">Low</option>
                        <option data-id="element-2658">Medium</option>
                        <option data-id="element-2659">High</option>
                        <option data-id="element-2660">Critical</option>
                      </select>
                    </div>
                  </div>

                  <div data-id="element-2661">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2662">
                      Description
                    </label>
                    <textarea required rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal text-sm resize-none" placeholder="Please provide as much detail as possible..." data-id="element-2663">
                  </textarea>
                  </div>

                  <button type="submit" disabled={isSubmitting} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-mavis-teal text-white font-medium rounded-none hover:bg-mavis-teal/90 transition-colors disabled:opacity-70" data-id="element-2664">

                    {isSubmitting ? 'Submitting...' : <>
                        <SendIcon className="w-4 h-4" data-id="element-2665" />
                        Submit Ticket
                      </>}
                  </button>
                </form>}
            </div>
          </div>
        </div>

        {/* Right Column: FAQ & Docs */}
        <div className="lg:col-span-2 space-y-8" data-id="element-2666">
          {/* FAQ Card */}
          <div className="bg-white rounded-none shadow-sm border border-gray-200" data-id="element-2667">
            <div className="p-6 border-b border-gray-100 flex items-center gap-3 bg-gray-50/50" data-id="element-2668">
              <HelpCircleIcon className="w-5 h-5 text-mavis-teal" data-id="element-2669" />
              <h2 className="text-lg font-bold text-gray-900" data-id="element-2670">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="divide-y divide-gray-100" data-id="element-2671">
              {faqs.map((faq, index) => {
              const isOpen = openFaq === index;
              return <div key={index} className="bg-white" data-id="element-2672">
                    <button onClick={() => setOpenFaq(isOpen ? null : index)} className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors" data-id="element-2673">

                      <span className="font-medium text-gray-900 pr-8" data-id="element-2674">
                        {faq.question}
                      </span>
                      <ChevronDownIcon className={`w-5 h-5 text-gray-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-2675" />

                    </button>
                    {isOpen && <div className="px-4 pb-4 pt-1 text-sm text-gray-600 leading-relaxed bg-gray-50/30" data-id="element-2676">
                        {faq.answer}
                      </div>}
                  </div>;
            })}
            </div>
          </div>

          {/* Documentation Links Card */}
          <div className="bg-white rounded-none shadow-sm border border-gray-200" data-id="element-2677">
            <div className="p-6 border-b border-gray-100 flex items-center gap-3 bg-gray-50/50" data-id="element-2678">
              <BookOpenIcon className="w-5 h-5 text-mavis-teal" data-id="element-2679" />
              <h2 className="text-lg font-bold text-gray-900" data-id="element-2680">
                Documentation & Resources
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2681">
              {[{
              title: 'User Guide',
              desc: 'Complete guide to using MAVIS',
              icon: FileTextIcon
            }, {
              title: 'API Documentation',
              desc: 'REST API reference and examples',
              icon: CodeIcon
            }, {
              title: 'Release Notes',
              desc: 'Latest updates and changes',
              icon: GitBranchIcon
            }, {
              title: 'Training Videos',
              desc: 'Step-by-step video tutorials',
              icon: VideoIcon
            }].map((resource, i) => {
              const Icon = resource.icon;
              return <div key={i} className="flex items-start gap-4 p-4 border border-gray-200 hover:border-mavis-teal/30 hover:bg-mavis-teal/5 cursor-pointer transition-colors group" data-id="element-2682">

                    <div className="w-10 h-10 bg-gray-100 group-hover:bg-white flex items-center justify-center shrink-0 transition-colors border border-transparent group-hover:border-mavis-teal/20" data-id="element-2683">
                      <Icon className="w-5 h-5 text-gray-500 group-hover:text-mavis-teal transition-colors" data-id="element-2684" />
                    </div>
                    <div data-id="element-2685">
                      <h3 className="font-bold text-gray-900 group-hover:text-mavis-teal transition-colors" data-id="element-2686">
                        {resource.title}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-2687">
                        {resource.desc}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </div>;
}